'use client';

import React from 'react';
import { useIntl } from 'react-intl';
import { Drawer, Icon } from '@ui-library';
import { Ban } from '@ui-library/icons';

import useLocalization from '@/hooks/useLocalization';
import { useCancellationPolicy } from '@/hooks/queries/useCancellationPolicy';
import { ReservationWithRateDetailsDTO } from '@/models';
import { toApiLanguageCode } from '@/providers/i18n/utils';

import { CancellationPolicyContent } from './CancellationPolicyContent';
import { CancellationPolicyErrorBoundary } from './CancellationPolicyErrorBoundary';
import { canRenderCancellationTimeline } from './cancellationTimeline';

type CancellationPolicySheetProps = {
  reservation: ReservationWithRateDetailsDTO;
  open: boolean;
  onClose: () => void;
  propertyTimezone?: string;
  propertyDateFormat?: string;
};

const CancellationPolicySheetBody: React.FC<
  Omit<CancellationPolicySheetProps, 'onClose'>
> = ({ reservation, open, propertyTimezone, propertyDateFormat }) => {
  const { locale, dateFnsLocale } = useLocalization();

  const { policyInformation } = useCancellationPolicy({
    propertyId: reservation.propertyId,
    reservationId: reservation.id,
    languageCode: toApiLanguageCode(locale),
    enabled: open,
  });

  const policyText = policyInformation?.policyText ?? null;

  if (
    !canRenderCancellationTimeline(policyInformation, propertyTimezone) ||
    !policyText ||
    !propertyTimezone
  ) {
    return null;
  }

  return (
    <CancellationPolicyContent
      policyText={policyText}
      timeZone={propertyTimezone}
      locale={dateFnsLocale}
      dateFormat={propertyDateFormat}
    />
  );
};

/**
 * The BOOK-8062 "Cancellation policy" bottom sheet — opened from the policy row in the reservation
 * drawer. Bottom sheet on mobile, side drawer on desktop (the shared Drawer default).
 */
export const CancellationPolicySheet: React.FC<
  CancellationPolicySheetProps
> = ({ reservation, open, onClose, propertyTimezone, propertyDateFormat }) => {
  const intl = useIntl();

  return (
    <Drawer
      title={intl.formatMessage({
        id: 'edit_reservation/reservation_details/cancellation_policy_label',
        defaultMessage: 'Cancellation policy',
      })}
      titleIcon={
        <Icon color="fog.800">
          <Ban />
        </Icon>
      }
      size="sm"
      open={open}
      onOpenChange={(e) => {
        if (!e.open) {
          onClose();
        }
      }}
    >
      <CancellationPolicyErrorBoundary>
        <CancellationPolicySheetBody
          reservation={reservation}
          open={open}
          propertyTimezone={propertyTimezone}
          propertyDateFormat={propertyDateFormat}
        />
      </CancellationPolicyErrorBoundary>
    </Drawer>
  );
};
